import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  Typography,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { cpeService } from '../services/cpeService';
import zoneService from '../services/zoneService';
import { userService } from '../services/userService';
import nextActionOptionService from '../services/nextActionOptionService';
import {
  CPEFormData,
  PLANT_TYPES,
  CPE_STATUSES,
  COMMERCIAL_STATUSES,
  INTERNAL_POSITIONING_OPTIONS,
  PlantType,
  InternalPositioning,
  CPEStatus,
  CommercialStatus,
  NextActionOption,
} from '../types/cpe';
import { User } from '../types/user';
import { Zone } from '../types/zone';
import { useAuth } from '../contexts/AuthContext';

interface ZoneOption {
  id: string;
  name: string;
  color: string;
}

interface CPEFormDialogProps {
  open: boolean;
  onClose: () => void;
  cpeId?: string;
  initialLatitude?: string;
  initialLongitude?: string;
  embedded?: boolean;
  onCreated?: () => void;
  defaultZone?: Zone | null;
}

export const CPEFormDialog: React.FC<CPEFormDialogProps> = ({
  open,
  onClose,
  cpeId,
  initialLatitude,
  initialLongitude,
  embedded,
  onCreated,
  defaultZone,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEdit = !!cpeId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [zones, setZones] = useState<ZoneOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [nextActionOptions, setNextActionOptions] = useState<NextActionOption[]>([]);

  const [formData, setFormData] = useState<CPEFormData>({
    name: '',
    latitude: initialLatitude || '',
    longitude: initialLongitude || '',
    plant_type: [],
    internal_positioning: null,
    cpe_status: null,
    power: '',
    short_description: '',
    notes: '',
    company_cui: '',
    epc: '',
    dispatcher: '',
    maintenance_company: '',
    project_link: '',
    internal_link: '',
    exact_location: true,
    is_waldevar_pp: false,
    commercial_status: null,
    om_account_manager: user?.id || '',
    energization_year: '',
    zone_id: null,
    next_action_id: null,
    next_action_due_date: null,
  });

  const loadCPE = useCallback(async () => {
    if (!cpeId) return;
    try {
      setLoading(true);
      const cpe = await cpeService.getCPEById(cpeId);
      setFormData({
        name: cpe.name,
        latitude: cpe.latitude,
        longitude: cpe.longitude,
        plant_type: cpe.plant_type,
        internal_positioning: cpe.internal_positioning,
        cpe_status: cpe.cpe_status,
        power: cpe.power || '',
        short_description: cpe.short_description || '',
        notes: cpe.notes || '',
        company_cui: cpe.company_cui || '',
        epc: cpe.epc || '',
        dispatcher: cpe.dispatcher || '',
        maintenance_company: cpe.maintenance_company || '',
        project_link: cpe.project_link || '',
        internal_link: cpe.internal_link || '',
        exact_location: cpe.exact_location ?? true,
        is_waldevar_pp: cpe.is_waldevar_pp ?? false,
        commercial_status: cpe.commercial_status,
        om_account_manager: cpe.om_account_manager || '',
        energization_year: cpe.energization_year || '',
        zone_id: cpe.zone_id || null,
        next_action_id: cpe.next_action_id || null,
        next_action_due_date: cpe.next_action_due_date || null,
      });
      setCurrentUpdatedAt(cpe.updated_at.toString());
      if (cpe.image_url) {
        setImagePreview(cpeService.getImageUrl(cpe.image_url));
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to load CPE');
    } finally {
      setLoading(false);
    }
  }, [cpeId]);

  useEffect(() => {
    if (isEdit && cpeId && open) {
      loadCPE();
    } else if (!open) {
      // Reset form when dialog closes
      setFormData({
        name: '',
        latitude: '',
        longitude: '',
        plant_type: [],
        internal_positioning: null,
        cpe_status: null,
        power: '',
        short_description: '',
        notes: '',
        company_cui: '',
        epc: '',
        dispatcher: '',
        maintenance_company: '',
        project_link: '',
        internal_link: '',
        exact_location: true,
        is_waldevar_pp: false,
        commercial_status: null,
        om_account_manager: user?.id || '',
        energization_year: '',
        zone_id: null,
        next_action_id: null,
        next_action_due_date: null,
      });
      setImageFile(null);
      setImagePreview(null);
      setError('');
      setFieldErrors({});
    }
  }, [cpeId, open, isEdit, loadCPE, user]);

  // Also update default when user info becomes available while dialog is open
  useEffect(() => {
    if (open && !isEdit && user) {
      setFormData(prev => ({ ...prev, om_account_manager: prev.om_account_manager || user.id }));
    }
  }, [open, isEdit, user]);

  // Update coordinates when initial values change (from map click)
  useEffect(() => {
    if (!isEdit && open && (initialLatitude || initialLongitude)) {
      setFormData(prev => ({
        ...prev,
        latitude: initialLatitude || prev.latitude,
        longitude: initialLongitude || prev.longitude,
      }));
    }
  }, [initialLatitude, initialLongitude, open, isEdit]);

  // Set default zone when defaultZone prop changes
  useEffect(() => {
    if (!isEdit && open && defaultZone) {
      setFormData(prev => ({
        ...prev,
        zone_id: defaultZone.id,
      }));
    }
  }, [defaultZone, open, isEdit]);

  useEffect(() => {
    if (open) {
      loadZones();
      loadUsers();
      loadNextActionOptions();
    }
  }, [open]);

  const loadZones = async () => {
    try {
      const zonesList = await zoneService.getZonesList();
      setZones(zonesList.map(z => ({ id: z.id, name: z.name, color: z.color })));
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersList = await userService.getAllUsers();
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadNextActionOptions = async () => {
    try {
      const opts = await nextActionOptionService.getAll();
      // Store all options; render filters to active-only (archived only appear if already assigned)
      setNextActionOptions(opts);
    } catch (error) {
      console.error('Error loading next action options:', error);
    }
  };

  const handleChange = (field: keyof CPEFormData, value: string | number | boolean | null | PlantType[] | InternalPositioning | CPEStatus | CommercialStatus) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setFieldErrors({});

    // Comprehensive validation before any sanitization
    const errors: Record<string, string> = {};

    // Name validation
    if (!formData.name || formData.name.trim() === '') {
      errors.name = t('validation.required', 'This field is required');
    } else if (formData.name.length > 255) {
      errors.name = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: 255 });
    } else if (formData.name.trim().length < 2) {
      errors.name = t('validation.minLength', 'Minimum length is {{min}} characters', { min: 2 });
    } else {
      // Check for excessive special characters (more than 30% of the name)
      const specialChars = formData.name.match(/[^a-zA-Z0-9\s\-_.]/g);
      if (specialChars && specialChars.length > formData.name.length * 0.3) {
        errors.name = t('validation.tooManySpecialChars', 'Name contains too many special characters');
      }
    }

    // Latitude validation
    const latValue = formData.latitude?.toString().trim() || '';
    if (!latValue) {
      errors.latitude = t('validation.required', 'This field is required');
    } else {
      const lat = parseFloat(latValue);
      if (isNaN(lat)) {
        errors.latitude = t('validation.invalidNumber', 'Must be a valid number');
      } else if (lat < -90 || lat > 90) {
        errors.latitude = t('validation.latitudeRange', 'Latitude must be between -90 and 90');
      }
    }

    // Longitude validation
    const lngValue = formData.longitude?.toString().trim() || '';
    if (!lngValue) {
      errors.longitude = t('validation.required', 'This field is required');
    } else {
      const lng = parseFloat(lngValue);
      if (isNaN(lng)) {
        errors.longitude = t('validation.invalidNumber', 'Must be a valid number');
      } else if (lng < -180 || lng > 180) {
        errors.longitude = t('validation.longitudeRange', 'Longitude must be between -180 and 180');
      }
    }

    // Plant Type validation - required by backend model
    if (!formData.plant_type || formData.plant_type.length === 0) {
      errors.plant_type = t('validation.atLeastOne', 'At least one plant type is required');
    }

    // Power validation (if provided)
    if (formData.power && formData.power !== '') {
      const powerValue = parseFloat(formData.power.toString());
      if (isNaN(powerValue)) {
        errors.power = t('validation.invalidNumber', 'Must be a valid number');
      } else if (powerValue < 0) {
        errors.power = t('validation.mustBePositive', 'Must be a positive number');
      } else if (powerValue > 999999999.99) {
        errors.power = t('validation.tooLarge', 'Value is too large');
      }
    }

    // Energization Year validation (if provided)
    if (formData.energization_year && formData.energization_year !== '') {
      const year = parseInt(formData.energization_year.toString(), 10);
      if (isNaN(year)) {
        errors.energization_year = t('validation.invalidNumber', 'Must be a valid year');
      } else if (year < 1900) {
        errors.energization_year = t('validation.yearTooOld', 'Year cannot be before 1900');
      } else if (year > 2100) {
        errors.energization_year = t('validation.yearTooFar', 'Year cannot be after 2100');
      }
    }

    // Company CUI validation (if provided)
    if (formData.company_cui && formData.company_cui.trim().length > 0) {
      if (formData.company_cui.length > 100) {
        errors.company_cui = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: 100 });
      }
      // Basic CUI format validation (Romanian company unique identifier)
      const cuiPattern = /^[A-Z0-9\s-]+$/i;
      if (!cuiPattern.test(formData.company_cui)) {
        errors.company_cui = t('validation.invalidFormat', 'Invalid format. Use only letters, numbers, spaces and dashes');
      }
    }

    // Project link validation (if provided)
    if (formData.project_link && formData.project_link.trim().length > 0) {
      if (formData.project_link.length > 500) {
        errors.project_link = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: 500 });
      }
      // Basic URL validation
      try {
        const url = new URL(formData.project_link.trim());
        if (!['http:', 'https:'].includes(url.protocol)) {
          errors.project_link = t('validation.invalidUrl', 'URL must start with http:// or https://');
        }
      } catch {
        errors.project_link = t('validation.invalidUrl', 'Invalid URL format');
      }
    }

    // Internal link validation (if provided)
    if (formData.internal_link && formData.internal_link.trim().length > 0) {
      if (formData.internal_link.length > 500) {
        errors.internal_link = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: 500 });
      }
      // Basic URL validation
      try {
        const url = new URL(formData.internal_link.trim());
        if (!['http:', 'https:'].includes(url.protocol)) {
          errors.internal_link = t('validation.invalidUrl', 'URL must start with http:// or https://');
        }
      } catch {
        errors.internal_link = t('validation.invalidUrl', 'Invalid URL format');
      }
    }

    // Text field length and content validations
    if (formData.short_description && formData.short_description.length > 1000) {
      errors.short_description = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: 1000 });
    }

    if (formData.notes && formData.notes.length > 5000) {
      errors.notes = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: 5000 });
    }

    // EPC, Dispatcher, Maintenance Company - validate reasonable text content
    const validateTextFieldContent = (value: string, fieldName: string, maxLength: number) => {
      if (value.length > maxLength) {
        errors[fieldName] = t('validation.maxLength', 'Maximum length is {{max}} characters', { max: maxLength });
      }
      // Check for excessive special characters or gibberish
      const specialChars = value.match(/[^a-zA-Z0-9\s\-_.,&()/]/g);
      if (specialChars && specialChars.length > value.length * 0.5) {
        errors[fieldName] = t('validation.invalidCharacters', 'Contains too many invalid characters');
      }
      // Check if it's mostly special characters (gibberish)
      const alphanumeric = value.match(/[a-zA-Z0-9]/g);
      if (value.trim().length > 0 && (!alphanumeric || alphanumeric.length < value.length * 0.3)) {
        errors[fieldName] = t('validation.invalidContent', 'Content appears to be invalid');
      }
    };

    if (formData.epc && formData.epc.trim().length > 0) {
      validateTextFieldContent(formData.epc, 'epc', 255);
    }

    if (formData.dispatcher && formData.dispatcher.trim().length > 0) {
      validateTextFieldContent(formData.dispatcher, 'dispatcher', 255);
    }

    if (formData.maintenance_company && formData.maintenance_company.trim().length > 0) {
      validateTextFieldContent(formData.maintenance_company, 'maintenance_company', 255);
    }

    // Next action validation: both must be set or both empty
    const hasAction = !!formData.next_action_id;
    const hasDueDate = !!formData.next_action_due_date && formData.next_action_due_date.trim() !== '';
    if (hasAction && !hasDueDate) {
      errors.next_action_due_date = t('nextAction.validation.dueDateRequired', 'Due date is required when a next action is set');
    }
    if (!hasAction && hasDueDate) {
      errors.next_action_id = t('nextAction.validation.actionRequired', 'Next action is required when a due date is set');
    }

    // If there are validation errors, display them and stop
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      // Create user-friendly error summary
      const errorFields = Object.keys(errors).map(key => {
        const fieldNames: Record<string, string> = {
          name: t('cpe.fields.name', 'Name'),
          latitude: t('cpe.fields.latitude', 'Latitude'),
          longitude: t('cpe.fields.longitude', 'Longitude'),
          plant_type: t('cpe.fields.plantType', 'Plant Type'),
          power: t('cpe.fields.power', 'Power'),
          energization_year: t('cpe.fields.energizationYear', 'Energization Year'),
          company_cui: t('cpe.fields.companyCui', 'Company CUI'),
          project_link: t('cpe.fields.projectLink', 'Project Link'),
          short_description: t('cpe.fields.shortDescription', 'Short Description'),
          notes: t('cpe.fields.notes', 'Notes'),
          epc: t('cpe.fields.epc', 'EPC'),
          dispatcher: t('cpe.fields.dispatcher', 'Dispatcher'),
          maintenance_company: t('cpe.fields.maintenanceCompany', 'Maintenance Company'),
        };
        return fieldNames[key] || key;
      });

      setError(
        t('validation.errorsFound', 'Please fix the following errors: {{fields}}', {
          fields: errorFields.join(', ')
        })
      );

      console.error('Validation errors:', errors); // Debug logging
      return;
    }

    try {
      setSaving(true);

      const data: CPEFormData & { latitude: number; longitude: number; power: number | null; energization_year: number | null } = {
        ...formData,
        latitude: parseFloat(formData.latitude.toString()),
        longitude: parseFloat(formData.longitude.toString()),
        power: formData.power ? parseFloat(formData.power.toString()) : null,
        energization_year: formData.energization_year
          ? parseInt(formData.energization_year.toString(), 10)
          : null,
        // Use backend defaults if fields are null/empty
        cpe_status: formData.cpe_status || 'Project',
        commercial_status: formData.commercial_status || 'Uncontacted',
      };

      let resultCpeId: string;

      if (isEdit && cpeId) {
        const updated = await cpeService.updateCPE(cpeId, data, currentUpdatedAt);
        resultCpeId = updated.id;
      } else {
        const created = await cpeService.createCPE(data);
        resultCpeId = created.id;
      }

      // Upload image if selected
      if (imageFile) {
        await cpeService.uploadCPEImage(resultCpeId, imageFile);
      }

      onClose();
      onCreated?.(); // Call the onCreated callback if provided
      // Don't navigate - stay on current page (map or list)
    } catch (err: unknown) {
      console.error('Error saving CPE:', err); // Debug logging
      const error = err as {
        response?: {
          data?: {
            errors?: Record<string, string>;
            message?: string
          };
          status?: number
        }
      };

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        setFieldErrors(backendErrors);

        // Create a user-friendly error message listing all validation errors
        const errorMessages = Object.entries(backendErrors).map(([field, message]) => {
          // Capitalize field name for display
          const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          return `${fieldName}: ${message}`;
        });

        setError(`Validation errors:\n${errorMessages.join('\n')}`);
      } else if (error.response?.status === 409) {
        if (error.response?.data?.message?.includes('coordinates')) {
          setFieldErrors({
            latitude: 'These coordinates are already in use',
            longitude: 'These coordinates are already in use',
          });
          setError('A CPE already exists at these coordinates');
        } else {
          setError(
            'This CPE has been modified by another user. Please refresh and try again.'
          );
        }
      } else {
        // Generic error message
        const errorMessage = error.response?.data?.message || 'Failed to save CPE';
        setError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const dialogContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label={t('cpe.fields.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
          />
        </Grid>

        {/* Coordinates */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label={t('cpe.fields.latitude')}
            type="number"
            value={formData.latitude}
            onChange={(e) => handleChange('latitude', e.target.value)}
            error={!!fieldErrors.latitude}
            helperText={fieldErrors.latitude}
            inputProps={{ step: '0.000001' }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label={t('cpe.fields.longitude')}
            type="number"
            value={formData.longitude}
            onChange={(e) => handleChange('longitude', e.target.value)}
            error={!!fieldErrors.longitude}
            helperText={fieldErrors.longitude}
            inputProps={{ step: '0.000001' }}
          />
        </Grid>

        {/* Plant Type */}
        <Grid item xs={12}>
          <FormControl fullWidth required error={!!fieldErrors.plant_type}>
            <InputLabel>{t('cpe.fields.plantType')}</InputLabel>
            <Select
              multiple
              value={formData.plant_type}
              onChange={(e) => handleChange('plant_type', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.plantType')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {PLANT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={formData.plant_type.indexOf(type) > -1} />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.plant_type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {fieldErrors.plant_type}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Internal Positioning */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t('cpe.fields.internalPositioning')}</InputLabel>
            <Select
              value={formData.internal_positioning || ''}
              onChange={(e) => handleChange('internal_positioning', e.target.value || null)}
              label={t('cpe.fields.internalPositioning')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {INTERNAL_POSITIONING_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === 'go' ? t('cpe.positioning.go') : t('cpe.positioning.noGo')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* CPE Status */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t('cpe.fields.cpeStatus')}</InputLabel>
            <Select
              value={formData.cpe_status || ''}
              onChange={(e) => handleChange('cpe_status', e.target.value || null)}
              label={t('cpe.fields.cpeStatus')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {CPE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Power */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.power')}
            type="number"
            value={formData.power}
            onChange={(e) => handleChange('power', e.target.value)}
            error={!!fieldErrors.power}
            helperText={fieldErrors.power || t('cpe.hints.powerUnit', 'Power in MW')}
            inputProps={{ step: '0.01', min: '0' }}
          />
        </Grid>

        {/* Energization Year */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.energizationYear')}
            type="number"
            value={formData.energization_year}
            onChange={(e) => handleChange('energization_year', e.target.value)}
            error={!!fieldErrors.energization_year}
            helperText={fieldErrors.energization_year}
            inputProps={{ min: 1900, max: 2100, step: '1' }}
          />
        </Grid>

        {/* Zone */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('cpe.fields.zone')}</InputLabel>
            <Select
              value={formData.zone_id || ''}
              onChange={(e) => handleChange('zone_id', e.target.value || null)}
              displayEmpty
              input={<OutlinedInput label={t('cpe.fields.zone')} notched />}
            >
              <MenuItem value="">
                <em>{t('common.none')}</em>
              </MenuItem>
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: zone.color,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                    {zone.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Next Action */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!fieldErrors.next_action_id}>
            <InputLabel shrink>{t('nextAction.label', 'Next Action')}</InputLabel>
            <Select
              value={formData.next_action_id || ''}
              onChange={(e) => {
                const val = e.target.value || null;
                handleChange('next_action_id', val);
                // Clear due date when action is cleared
                if (!val) handleChange('next_action_due_date', null);
              }}
              displayEmpty
              input={<OutlinedInput label={t('nextAction.label', 'Next Action')} notched />}
            >
              <MenuItem value="">
                <em>{t('nextAction.noAction', 'No action set')}</em>
              </MenuItem>
              {nextActionOptions
                .filter((o) => o.is_active || o.id === formData.next_action_id)
                .map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.next_action_id && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {fieldErrors.next_action_id}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Next Action Due Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('nextAction.dueDate', 'Next Action Due Date')}
            type="date"
            value={formData.next_action_due_date || ''}
            onChange={(e) => handleChange('next_action_due_date', e.target.value || null)}
            error={!!fieldErrors.next_action_due_date}
            helperText={fieldErrors.next_action_due_date}
            disabled={!formData.next_action_id}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Commercial Status */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>{t('cpe.fields.commercialStatus')}</InputLabel>
            <Select
              value={formData.commercial_status || ''}
              onChange={(e) => handleChange('commercial_status', e.target.value || null)}
              label={t('cpe.fields.commercialStatus')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {COMMERCIAL_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Short Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('cpe.fields.shortDescription')}
            multiline
            rows={2}
            value={formData.short_description}
            onChange={(e) => handleChange('short_description', e.target.value)}
            error={!!fieldErrors.short_description}
            helperText={
              fieldErrors.short_description ||
              (formData.short_description ? `${formData.short_description.length}/1000` : '')
            }
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('cpe.fields.notes')}
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            error={!!fieldErrors.notes}
            helperText={
              fieldErrors.notes ||
              (formData.notes ? `${formData.notes.length}/5000` : '')
            }
          />
        </Grid>

        {/* Company CUI */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.companyCui')}
            value={formData.company_cui}
            onChange={(e) => handleChange('company_cui', e.target.value)}
            error={!!fieldErrors.company_cui}
            helperText={fieldErrors.company_cui}
            placeholder="RO12345678"
          />
        </Grid>

        {/* EPC */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.epc')}
            value={formData.epc}
            onChange={(e) => handleChange('epc', e.target.value)}
            error={!!fieldErrors.epc}
            helperText={fieldErrors.epc}
          />
        </Grid>

        {/* Dispatcher */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.dispatcher')}
            value={formData.dispatcher}
            onChange={(e) => handleChange('dispatcher', e.target.value)}
            error={!!fieldErrors.dispatcher}
            helperText={fieldErrors.dispatcher}
          />
        </Grid>

        {/* Maintenance Company */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.maintenanceCompany')}
            value={formData.maintenance_company}
            onChange={(e) => handleChange('maintenance_company', e.target.value)}
            error={!!fieldErrors.maintenance_company}
            helperText={fieldErrors.maintenance_company}
          />
        </Grid>

        {/* Exact Location Checkbox */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '56px' }}>
              <Checkbox
                checked={formData.exact_location}
                onChange={(e) => handleChange('exact_location', e.target.checked)}
              />
              <Typography>{t('cpe.fields.exactLocation', 'Exact Location')}</Typography>
            </Box>
          </FormControl>
        </Grid>

        {/* Waldevar PP Checkbox */}
        <Grid item xs={12} sm={6}>
          <FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '56px' }}>
              <Checkbox
                checked={formData.is_waldevar_pp}
                onChange={(e) => handleChange('is_waldevar_pp', e.target.checked)}
              />
              <Typography>{t('cpe.fields.isWaldevarPP', 'Waldevar PP')}</Typography>
            </Box>
          </FormControl>
        </Grid>

        {/* Internal Link (for Waldevar PPs) */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.internalLink', 'Internal Link')}
            value={formData.internal_link}
            onChange={(e) => handleChange('internal_link', e.target.value)}
            error={!!fieldErrors.internal_link}
            helperText={fieldErrors.internal_link || t('cpe.hints.internalLink', 'SharePoint/Teams link')}
            placeholder="https://waldevar.sharepoint.com/..."
          />
        </Grid>

        {/* External Link (formerly Project Link) */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('cpe.fields.externalLink', 'External Link')}
            value={formData.project_link}
            onChange={(e) => handleChange('project_link', e.target.value)}
            error={!!fieldErrors.project_link}
            helperText={fieldErrors.project_link}
            placeholder="https://..."
          />
        </Grid>

        {/* O&M Account Manager */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('cpe.fields.omAccountManager')}</InputLabel>
            <Select
              value={formData.om_account_manager || ''}
              onChange={(e) => handleChange('om_account_manager', e.target.value || null)}
              displayEmpty
              input={<OutlinedInput label={t('cpe.fields.omAccountManager')} notched />}
            >
              <MenuItem value="">
                <em>{t('common.none')}</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name || user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('cpe.image')}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
            >
              {imagePreview ? t('common.upload') : t('common.selectImage')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageSelect}
              />
            </Button>
            {imagePreview && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {t('cpe.imageResize', 'Image will be resized to max 512x512 pixels')}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );

  return embedded ? (
    // When embedded inside the TabbedCreateModal we need to render the form plus action buttons
    <Box sx={{ p: 2 }}>
      {dialogContent}

      {/* Embedded actions so Create/Cancel are reachable inside the parent dialog content */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loading}
        >
          {saving ? t('common.loading') : isEdit ? t('cpe.editCpe') : t('cpe.createNew')}
        </Button>
      </Box>
    </Box>
  ) : (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{isEdit ? t('cpe.editCpe') : t('cpe.createNew')}</Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          dialogContent
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loading}
        >
          {saving ? t('common.loading') : isEdit ? t('cpe.editCpe') : t('cpe.createNew')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
